<template>
    <RedesignBaseModal
        data-testid="global-confirm-modal"
        :visible="isOpen"
        :width="MODAL_WIDTH"
        wrapper-classes="confirm-modal"
        append-to-body
        @close="closeModal"
    >
        <template #title>
            <div
                data-testid="confirm-modal-title"
                class="text-xl font-semibold main-text-color confirm-modal-title"
            >
                {{ header || defaultHeader }}
            </div>
        </template>
        <div class="my-8 flex flex-col items-center justify-center">
            <div
                v-if="confirmModalState.descriptionIcon"
                class="mb-5"
            >
                <RedesignBaseIcon
                    :name="confirmModalState.descriptionIcon"
                    :size="64"
                    class="w-16 h-16 text-secondary-actions"
                />
            </div>
            <div
                data-testid="confirm-modal-message"
                class="text-center main-text-color text-base max-w-65-p break-normal"
            >
                {{ message || defaultDescription }}
            </div>
        </div>
        <template #footer>
            <div class="w-full flex items-center justify-between">
                <RedesignBaseButton
                    data-testid="confirm-modal-cancel-button"
                    :type="currentTypeData.cancelType || 'outline'"
                    :color="currentTypeData.cancelColor || 'secondary'"
                    :icon="currentTypeData.iconCancel"
                    @click="closeModal"
                >
                    {{ $t(currentTypeData.cancelActionKey) }}
                </RedesignBaseButton>
                <RedesignBaseButton
                    data-test-name="confirm-modal-confirm-button"
                    data-testid="confirm-modal-confirm-button"
                    :type="currentTypeData.type"
                    :color="currentTypeData.color"
                    :icon="currentTypeData.iconConfirm"
                    @click="confirm"
                >
                    {{ $t(currentTypeData.actionKey) }}
                </RedesignBaseButton>
            </div>
        </template>
    </RedesignBaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { confirmModalState, ConfirmServiceTypeType } from '@/composables/useConfirmModal'
import i18n from '@/i18n'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import { TButtonColor, TButtonType } from '@/components/RedesignBaseComponents/Buttons/buttons'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseModal from '@/components/RedesignComponents/Modal/RedesignBaseModal.vue'

const MODAL_WIDTH = '456px'

type ConfirmModalTypeData = {
    color: TButtonColor
    type?: TButtonType
    cancelType?: TButtonType
    cancelColor?: TButtonColor
    iconConfirm: string
    iconCancel: string
    actionKey: string
    cancelActionKey: string
    autofocusCancel?: boolean
}

const MODAL_DATA_DEFAULT: Record<ConfirmServiceTypeType, ConfirmModalTypeData> = {
    confirm: {
        color: 'primary',
        iconConfirm: 'Check',
        iconCancel: 'X',
        actionKey: 'common.confirm',
        cancelActionKey: 'common.cancel'
    },
    save: {
        color: 'primary',
        iconConfirm: 'Save',
        iconCancel: 'X',
        actionKey: 'common.save',
        cancelActionKey: 'common.cancel'
    },
    edit: {
        color: 'primary',
        iconConfirm: 'Check',
        iconCancel: 'X',
        actionKey: 'common.confirm',
        cancelActionKey: 'common.cancel'
    },
    delete: {
        color: 'destructive',
        iconConfirm: 'vc-icon-recycle-bin',
        iconCancel: 'X',
        actionKey: 'common.delete',
        cancelActionKey: 'common.cancel'
    },
    discard: {
        color: 'destructive',
        type: 'outline',
        cancelType: 'default',
        cancelColor: 'primary',
        iconConfirm: '',
        iconCancel: '',
        actionKey: 'dialog.discardChanges.discard',
        cancelActionKey: 'dialog.discardChanges.keepEditing',
        autofocusCancel: true
    }
}

const currentTypeData = computed(() => {
    const _type = confirmModalState.value.type as ConfirmServiceTypeType
    return MODAL_DATA_DEFAULT[_type] || MODAL_DATA_DEFAULT.confirm
})

const isOpen = computed(() => {
    return confirmModalState.value.open
})
const header = computed(() => {
    return confirmModalState.value.header
})
const message = computed(() => {
    return confirmModalState.value.message
})
const defaultHeader = computed(() => {
    return i18n.t('general.defaultConfirmation')
})
const defaultDescription = computed(() => {
    return i18n.t('dialog.confirm.description')
})

const emit = defineEmits([ 'close', 'confirm' ])

const closeModal = () => {
    if (confirmModalState.value.onClose !== undefined) {
        confirmModalState.value.onClose()
    }

    emit('close')
}
const confirm = () => {
    if (confirmModalState.value.onConfirm !== undefined) {
        confirmModalState.value.onConfirm()
    }
    emit('confirm')
}
</script>

<style lang="scss">

</style>
